/**
 * 手で直したマッチ度 (IN-M-010・DP-M-004・DP-M-014) の読み方と、入力の検査
 * (画面レイアウト_SCR-M-001.md §4.2・§5.1 No.7・§6.4.5、Issue #1435 段階 ②)。
 */

const SCORE_MIN = 0;
const SCORE_MAX = 100;
// 全角の数字 (０ = U+FF10) と半角の数字 (0 = U+0030) の文字コードの差
const FULLWIDTH_DIGIT_OFFSET = 0xff10 - 0x30;

// §5.1 No.7 の文言
export const SCORE_INPUT_ERROR_MESSAGE = '0〜100 の整数で入力してください。';

/**
 * 推薦の要素から、手で直した値を読む。0〜100 の整数のときだけ「直した値がある」とし、
 * キーが無い・null・それ以外の値は「直していない」(null) にする (§6.4.5「手で直した値の読み方」)。
 *
 * @param {object|null|undefined} recommendation
 * @returns {number|null}
 */
export function readScoreOverride(recommendation) {
    const value = recommendation?.score_override;
    return Number.isInteger(value) && value >= SCORE_MIN && value <= SCORE_MAX ? value : null;
}

/**
 * AI が出した値。BE は 0〜100 のスケールで返す (ここで 100 倍しない。Issue #537)。
 *
 * @param {object|null|undefined} recommendation
 * @returns {number}
 */
export function getAiScore(recommendation) {
    return Math.round(recommendation?.score ?? 0);
}

/**
 * 画面に出す値。手で直した値があればその値、無ければ AI の値 (推薦 PDF と同じ決め方。PBI-002 §11.8)。
 *
 * @param {object|null|undefined} recommendation
 * @returns {number}
 */
export function getDisplayedScore(recommendation) {
    const override = readScoreOverride(recommendation);
    return override !== null ? override : getAiScore(recommendation);
}

/**
 * 入力欄の文字を、保存する値に変える。0〜100 の整数でなければ null (§5.1 No.7)。
 * 前後の空白は除く。全角の数字 (０〜９) は半角に直してから検査する (日本語入力のまま打った数を受け付ける)。
 * 直した後が数字 1〜3 桁のときだけ受け付ける (空・小数・符号・数字以外は合わない)。
 *
 * @param {string} text
 * @returns {number|null}
 */
export function parseScoreInput(text) {
    const trimmed = String(text ?? '')
        .trim()
        .replace(/[０-９]/g, (digit) => String.fromCharCode(digit.charCodeAt(0) - FULLWIDTH_DIGIT_OFFSET));
    if (!/^[0-9]{1,3}$/.test(trimmed)) return null;
    const value = Number(trimmed);
    return value <= SCORE_MAX ? value : null;
}

/**
 * 推薦の要素に、保存後の「手で直した値」を入れた新しい要素を返す (元の要素は変えない)。
 * 直していない (null) なら、キーごと外す。
 *
 * @param {object} recommendation
 * @param {number|null} override
 * @returns {object}
 */
export function withScoreOverride(recommendation, override) {
    const { score_override: _removed, ...rest } = recommendation;
    return override === null ? rest : { ...rest, score_override: override };
}
